import { isNativeApp } from "@/lib/nativeBridge";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import InquireButton from "@/components/InquireButton";
import BookVideoTourDialog from "@/components/BookVideoTourDialog";
import AddToCalendarButton from "@/components/AddToCalendarButton";
import PdfModal from "@/components/PdfModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { DogIcon as Dog, GenderMaleIcon as GenderMale, GenderFemaleIcon as GenderFemale, TrashIcon as Trash, PencilSimpleIcon as PencilSimple, TreeStructureIcon as TreeStructure, CheckCircleIcon as CheckCircle, ShareIcon as Share, FilePdfIcon as FilePdf, ArrowSquareOutIcon as ArrowSquareOut, CertificateIcon as Certificate, ArrowsLeftRightIcon as ArrowsLeftRight, ChatCircleIcon as ChatCircle, StorefrontIcon as Storefront, StarIcon as Star, CameraIcon as Camera, VideoCameraIcon as VideoCamera } from "@phosphor-icons/react";
import { formatDate, photoUrl, thumbUrl } from "@/lib/utils-app";
import api from "@/lib/api";
import { toast } from "sonner";
import PhotoLightbox from "@/components/PhotoLightbox";

export default function DogSidebar({
  dog,
  id,
  user,
  breeder,
  copied,
  onShare,
  purchasing,
  certAccess,
  onCertificateClick,
  regCertUrl,
  publicUrl,
  onOpenTransfer,
  onOpenListing,
  listingBusy = false,
  onFeature,
  featureBusy = false,
  onDelete,
  certPrice,
  onDogPatched,
}) {
  const isOwnerOrAdmin = dog?.created_by === user?.id || user?.role === "admin";
  const canEdit = isOwnerOrAdmin;
  const navigate = useNavigate();
  // Lightbox state for the photo gallery. Click any thumbnail (or the
  // hero image) to open at full resolution with prev/next controls.
  const [lightboxIndex, setLightboxIndex] = React.useState(null);
  const [tourOpen, setTourOpen] = useState(false);
  const openLightbox = (i) => setLightboxIndex(i);
  // Registration-certificate PDF modal. We render a portal-based
  // overlay instead of routing to /pdf-viewer to dodge iOS PWA
  // history-stack quirks that left users stranded on the viewer.
  const [showRegPdf, setShowRegPdf] = useState(false);
  return (
    <div className="bg-white border border-[#EAEAEA] rounded-xl overflow-hidden shadow-luxury-light md:sticky md:top-24 md:max-h-[calc(100vh-7rem)] md:overflow-y-auto md:overscroll-contain" data-testid="dog-sidebar">
      <div className="aspect-square bg-gradient-to-br from-[#FBF7EC] via-[#F3E9C7]/40 to-[#FBF7EC] relative">
        {dog.photos?.[0] ? (
          <button
            type="button"
            onClick={() => openLightbox(0)}
            className="block w-full h-full"
            data-testid="dog-hero-photo"
            aria-label="Open photo gallery"
          >
            <img src={photoUrl(dog.photos[0])} alt={dog.name} className="w-full h-full object-cover photo-luxury" />
          </button>
        ) : (
          // Luxury empty state: a much smaller cream block (so it doesn't
          // eat the entire mobile viewport), centered medallion-style
          // icon inside a gold-ring frame, and an "Add a portrait" CTA
          // that nudges the owner to upload one. Mirrors the brand
          // language used elsewhere (eyebrow caps, gold hairline).
          <div className="w-full h-full flex flex-col items-center justify-center px-6 py-8 text-center">
            <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-white border border-[#C9A227]/40 shadow-[0_4px_16px_rgba(193,162,39,0.12)]">
              <Dog size={36} weight="duotone" className="text-[#C9A227]" />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#8A6A00] mt-5">
              Portrait pending
            </p>
            <p className="text-[13px] text-ink/65 font-spectral italic mt-1.5 max-w-[20ch]">
              A good portrait makes every pedigree page sing.
            </p>
            {canEdit && (
              <button
                type="button"
                onClick={() => navigate(`/dogs/${dog.id}/edit`)}
                data-testid="dog-hero-add-photo"
                className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-ink hover:bg-crimson text-white text-[12px] font-semibold tracking-[0.04em] transition-colors"
              >
                <Camera size={14} weight="bold" />
                Add a portrait
              </button>
            )}
          </div>
        )}
      </div>
      <div className="p-5">
        {dog.photos?.length > 1 && (
          <div className="grid grid-cols-4 gap-2 mb-4">
            {dog.photos.slice(1, 5).map((p, i) => (
              // Bug fix: thumbnails 2-5 previously rendered `<img src={p}>`
              // — for storage-path-based photos this resolved to a relative
              // URL the browser couldn't load (404 → blank grey box).
              // Now we route every photo ref through `thumbUrl()` so the
              // backend file-server resolves it correctly, AND make every
              // thumbnail a button that opens the lightbox at that index.
              <button
                key={i}
                type="button"
                onClick={() => openLightbox(i + 1)}
                data-testid={`dog-thumb-${i + 1}`}
                className="aspect-square rounded-md overflow-hidden bg-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-gold transition-opacity hover:opacity-90"
                aria-label={`Open photo ${i + 2}`}
              >
                <img
                  src={thumbUrl(p)}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover photo-luxury"
                  // If the thumb 404s (e.g. legacy uploads pre-thumbnail
                  // generation), silently fall back to the full-size URL.
                  onError={(e) => { e.currentTarget.src = photoUrl(p); }}
                />
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 mb-2">
          {dog.sex === "male" ? (
            <GenderMale size={20} className="text-ink" />
          ) : (
            <GenderFemale size={20} className="text-crimson" />
          )}
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold">
            {dog.breed}
          </span>
        </div>
        <h1 className="font-spectral font-light text-3xl md:text-4xl tracking-tight leading-[1.05] mb-3">{dog.name}</h1>
        <div className="gold-hairline mb-3" />
        <div className="space-y-1 text-sm text-[#6B6B6B] font-tabular">
          {dog.dob && <div>Born {formatDate(dog.dob)}</div>}
          {dog.color && <div className="font-sans">Color: {dog.color}</div>}
          {dog.microchip && <div className="font-mono">Chip: {dog.microchip}</div>}
          {dog.registration_number && (
            <div className="font-mono">Reg #: {dog.registration_number}</div>
          )}
          {dog.current_owner && <div className="font-sans">Owner: {dog.current_owner}</div>}
          {breeder && (
            <div className="font-sans">
              Breeder:{" "}
              <Link to={`/breeders/${breeder.id}`} className="text-ink hover:text-crimson underline-offset-4 hover:underline">
                {breeder.kennel_name || breeder.name}
              </Link>
            </div>
          )}
        </div>
        <div className="flex gap-2 mt-5">
          {isOwnerOrAdmin && (
            <Link to={`/dogs/${id}/edit`} className="flex-1">
              <Button
                variant="outline"
                className="w-full rounded-full border-[#E5E5E5]"
                data-testid="edit-dog-btn"
              >
                <PencilSimple size={14} className="mr-1" /> Edit
              </Button>
            </Link>
          )}
          <Link to={`/pedigree/${id}`} className="flex-1">
            <Button
              className="w-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full"
              data-testid="view-pedigree-btn"
            >
              <TreeStructure size={14} className="mr-1" /> Pedigree
            </Button>
          </Link>
        </div>
        <div className={`grid ${isOwnerOrAdmin ? "grid-cols-2" : "grid-cols-1"} gap-2 mt-2`}>
          <Button
            onClick={onShare}
            variant="outline"
            className="w-full rounded-full border-[#E5E5E5]"
            data-testid="dog-share-btn"
          >
            {copied ? (
              <>
                <CheckCircle size={14} className="mr-1" weight="fill" /> Copied
              </>
            ) : (
              <>
                <Share size={14} className="mr-1" /> Share
              </>
            )}
          </Button>
          {isOwnerOrAdmin && (
            <Button
              onClick={onCertificateClick}
              variant="outline"
              disabled={purchasing}
              className="w-full rounded-full border-[#C1272D]/30 text-crimson hover:bg-[#C1272D]/5 px-2 text-[12px]"
              data-testid="dog-certificate-btn"
            >
              <FilePdf size={13} className="mr-1 flex-shrink-0" weight="duotone" />
              <span className="truncate">
                {purchasing
                  ? "Redirecting…"
                  : certAccess?.granted
                  ? "Pedigree PDF"
                  : isNativeApp()
                  ? "Pedigree PDF"
                  : `Pedigree PDF · $${(certPrice ?? 14.99).toFixed(2)}`}
              </span>
            </Button>
          )}
        </div>
        {/* Owner-only toggle: include the dog's primary photo as a
            medallion in the registration certificate. Updates the dog
            record optimistically so the next click on the cert button
            below renders with the new state. We deliberately put this
            ABOVE the cert button so the cause→effect is visible. */}
        {isOwnerOrAdmin && dog.photos?.length > 0 && (
          <label
            className="mt-2 flex items-center justify-between gap-2 rounded-full border border-[#E5E5E5] bg-white px-3 py-2 cursor-pointer hover:border-[#C9A227]/55"
            data-testid="dog-cert-photo-toggle"
            htmlFor="cert-photo-toggle-input"
          >
            <span className="text-[12px] text-[#6B6B6B] truncate">Include dog photo on certificate</span>
            <input
              id="cert-photo-toggle-input"
              type="checkbox"
              className="h-4 w-4 accent-[#C9A227]"
              defaultChecked={!!dog.include_photo_on_cert}
              onChange={async (e) => {
                const next = e.target.checked;
                try {
                  // PUT just the toggle — DogUpdate accepts partial bodies
                  // so the rest of the dog record stays untouched.
                  await api.put(`/dogs/${id}`, { include_photo_on_cert: next });
                  // Tell the parent the dog mutated so subsequent renders
                  // (and the next cert download) see the new flag.
                  if (typeof onDogPatched === "function") {
                    onDogPatched({ include_photo_on_cert: next });
                  }
                  toast.success(next ? "Photo will appear on the certificate" : "Photo removed from certificate");
                } catch (err) {
                  e.target.checked = !next; // revert visually
                  toast.error("Couldn't update photo preference");
                }
              }}
            />
          </label>
        )}
        {isOwnerOrAdmin && (
          <button
            type="button"
            onClick={() => setShowRegPdf(true)}
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-[#C9A227] bg-[#FBF7EC] hover:bg-[#F3E8C5] text-[#8A6A00] text-sm font-semibold px-4 py-2 transition-colors"
            data-testid="dog-registration-cert-btn"
          >
            <Certificate size={14} weight="duotone" />
            Registration certificate
          </button>
        )}
        <PdfModal
          open={showRegPdf}
          onClose={() => setShowRegPdf(false)}
          src={regCertUrl}
          title={`Registration certificate · ${dog?.name || "Dog"}`}
          downloadName={`registration-${(dog?.name || id || "dog").toString().replace(/\s+/g, "-").toLowerCase()}.pdf`}
        />
        {isOwnerOrAdmin && (
          <Link
            to={`/feed?dog_id=${id}&text=${encodeURIComponent(`Hi everyone! I'm ${dog.name}. 🐾`)}`}
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-[#0E0E0E] bg-[#0E0E0E] hover:bg-[#C1272D] text-white text-sm font-semibold px-4 py-2 transition-colors"
            data-testid="dog-share-pawprints-btn"
          >
            <ChatCircle size={14} weight="duotone" />
            Share to The Dog's Voice
          </Link>
        )}
        {/* Buyer-side: "Show interest" CTA for non-owners viewing a listed dog. */}
        {!isOwnerOrAdmin && dog.for_sale && (
          <div className="mt-2">
            <InquireButton
              dog={dog}
              currentUserId={user?.id}
              variant="primary"
              testIdSuffix="sidebar"
            />
            <Button
              onClick={() => setTourOpen(true)}
              variant="outline"
              className="w-full rounded-full mt-2 border-[#0E0E0E] text-ink hover:bg-[#0E0E0E] hover:text-white"
              data-testid="dog-sidebar-book-tour-btn"
            >
              <VideoCamera size={14} className="mr-1" weight="duotone" />
              Book video tour · $35
            </Button>
            <BookVideoTourDialog
              open={tourOpen}
              onOpenChange={setTourOpen}
              dogId={id}
              dogName={dog.name}
            />
          </div>
        )}
        {isOwnerOrAdmin && (
          <Button
            onClick={onOpenListing}
            disabled={listingBusy}
            variant="outline"
            className={`w-full rounded-full mt-2 ${
              dog.for_sale
                ? "border-[#C9A227] bg-[#FBF7EC] text-[#8A6A00] hover:bg-[#F3E8C5]"
                : "border-[#E5E5E5]"
            }`}
            data-testid="dog-listing-btn"
          >
            <Storefront size={14} className="mr-1" weight="duotone" />
            {listingBusy
              ? "Processing…"
              : dog.for_sale
              ? `Listed${
                  dog.asking_price > 0
                    ? ` · $${Math.round(dog.asking_price).toLocaleString()}`
                    : ""
                }${
                  dog.sale_status && dog.sale_status !== "available"
                    ? ` · ${dog.sale_status}`
                    : ""
                }`
              : "List on marketplace · $9.99"}
          </Button>
        )}
        {isOwnerOrAdmin && dog.for_sale && !isNativeApp() && (() => {
          const isCurrentlyFeatured =
            dog.is_featured &&
            dog.featured_until &&
            new Date(dog.featured_until) > new Date();
          return (
            <Button
              onClick={onFeature}
              disabled={featureBusy || isCurrentlyFeatured}
              variant="outline"
              className={`w-full rounded-full mt-2 ${
                isCurrentlyFeatured
                  ? "border-[#C9A227] bg-gradient-to-r from-[#FBF7EC] to-[#FFF1C2] text-[#8A6A00]"
                  : "border-[#C9A227]/40 text-[#8A6A00] hover:bg-[#FBF7EC]"
              }`}
              data-testid="dog-feature-btn"
            >
              <Star
                size={14}
                className="mr-1"
                weight={isCurrentlyFeatured ? "fill" : "duotone"}
              />
              {featureBusy
                ? "Processing…"
                : isCurrentlyFeatured
                ? `Featured · ends ${new Date(dog.featured_until).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  })}`
                : "Promote to top · $29"}
            </Button>
          );
        })()}
        {isOwnerOrAdmin && (
          <Button
            onClick={onOpenTransfer}
            variant="outline"
            className="w-full rounded-full border-[#E5E5E5] mt-2"
            data-testid="dog-transfer-btn"
          >
            <ArrowsLeftRight size={14} className="mr-1" weight="duotone" />
            Transfer ownership
          </Button>
        )}
        {isOwnerOrAdmin && (
          <div className="mt-2">
            <AddToCalendarButton
              testId="dog-add-to-calendar"
              title={dog.name ? `${dog.name} — vet appointment` : ""}
              dogId={dog.id}
              kind="vet"
              label="Add reminder to calendar"
              variant="subtle"
              className="w-full justify-center"
            />
          </div>
        )}
        <a
          href={publicUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-2 text-xs text-[#6B6B6B] hover:text-ink inline-flex items-center gap-1"
          data-testid="dog-public-preview-link"
        >
          Preview public profile <ArrowSquareOut size={12} />
        </a>
        {isOwnerOrAdmin && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="w-full rounded-full border-[#C1272D]/30 text-crimson hover:bg-[#C1272D]/5 mt-2"
                data-testid="delete-dog-btn"
              >
                <Trash size={14} className="mr-1" /> Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {dog.name}?</AlertDialogTitle>
                <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onDelete} data-testid="confirm-delete-dog">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      <PhotoLightbox
        photos={dog?.photos || []}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onChange={setLightboxIndex}
      />
    </div>
  );
}
